import React from 'react'

import { Modal, useModal } from '@gousto-internal/citrus-react'
import actions from 'actions'
import { useDispatch, useSelector } from 'react-redux'
import { browserHistory } from 'react-router'

import { BasketChangesContent } from '@features/scrollable-basket'
import type { Recipe, RecipeUUID } from '@library/menu-service'

import { actionTypes } from 'actions/actionTypes'
import type { BasketRecipe } from 'hooks/basket/useBasketRecipes'
import { useBasketRecipes } from 'hooks/basket/useBasketRecipes'
import { useNumPortions } from 'hooks/basket/useNumPortions'
import { useMaxRecipesForPortion } from 'hooks/menu-boxes/useMaxRecipesForPortion'
import { useBasket } from 'routes/Menu/domains/basket'
import { createGetActionTypeIsPending } from 'selectors/status'

export const BasketChangesModal = () => {
  const { closeCurrentModal } = useModal()
  const dispatch = useDispatch()
  const { removeRecipeByUuid } = useBasket()
  const { numPortions } = useNumPortions()
  const maxRecipesNum = useMaxRecipesForPortion(numPortions)

  const isLoading = useSelector(createGetActionTypeIsPending(actionTypes.MENU_FETCH_DATA))

  const { basketRecipes } = useBasketRecipes()
  const selectedRecipes: Recipe[] = basketRecipes
    .map((basketItem: BasketRecipe) => basketItem.recipe)
    .filter((recipe): recipe is Recipe => Boolean(recipe))

  const isAnyRecipeNotAvailable = selectedRecipes.some((recipe) => !recipe.isAvailable)
  const isModalOpen = !isLoading && isAnyRecipeNotAvailable

  const goBack = () => {
    dispatch(actions.basketEditModalClosed(selectedRecipes, maxRecipesNum, numPortions))
    closeCurrentModal()
    browserHistory.goBack()
  }

  const submitRemovedRecipes = (recipeUUIDsToRemove: RecipeUUID[]) => {
    dispatch(
      actions.basketEditChangesConfirmed(
        selectedRecipes,
        recipeUUIDsToRemove,
        maxRecipesNum,
        numPortions,
      ),
    )
    closeCurrentModal()

    recipeUUIDsToRemove.forEach((uuid) => {
      removeRecipeByUuid(uuid, 'basket_changes_modal')
    })
  }

  const handleAfterOpen = () => {
    dispatch(actions.basketEditModalOpen(selectedRecipes, maxRecipesNum, numPortions))
  }

  return (
    <Modal
      shouldCloseOnOverlayClick={false}
      isOpen={isModalOpen}
      name="basket-changes"
      onAfterOpen={handleAfterOpen}
      preventScroll
    >
      <BasketChangesContent
        numberOfPortionsPerBox={numPortions}
        recipes={selectedRecipes}
        submitRemovedRecipes={submitRemovedRecipes}
        goBack={goBack}
      />
    </Modal>
  )
}
